import { Api } from '../../axios-config/AxiosConfig';
import { ApiException } from '../ApiException';

export interface ITenantDataFactory {
  id: string;
  idTenant: string;
  ativo: boolean;
  migration: string;
  historicosTenant: {
    ativo: boolean;
    usuario: number;
    data: string;
  }[];
  usuarioCriado: boolean;
  schema: string;
}

export type OrderDirection = 'asc' | 'desc';

export interface GetTenantsParams {
  IdTenant?: string;
  searchIdTenant?: string;
  Ativo?: boolean;
  SomenteAtivos?: boolean;
  order?: OrderDirection;
  sortBy?: string;
  limite?: number; // page size
  offset?: number; // page offset
}

const buildQueryParams = (p?: GetTenantsParams) => {
  if (!p) return undefined;
  const q: Record<string, unknown> = {};

  if (typeof p.IdTenant === 'string' && p.IdTenant.trim() !== '')
    q.IdTenant = p.IdTenant.trim();
  if (typeof p.searchIdTenant === 'string' && p.searchIdTenant.trim() !== '')
    q.searchIdTenant = p.searchIdTenant.trim();
  if (typeof p.Ativo === 'boolean') q.Ativo = p.Ativo;
  if (typeof p.SomenteAtivos === 'boolean') q.SomenteAtivos = p.SomenteAtivos;
  if (typeof p.order === 'string')
    q.order = p.order.toLowerCase() === 'desc' ? 'Desc' : 'Asc';
  if (typeof p.sortBy === 'string' && p.sortBy.trim() !== '') {
    const sb = p.sortBy.trim();
    q.sortBy = /^idtenant$/i.test(sb) ? 'IdTenant' : sb;
  }
  if (
    typeof p.limite === 'number' &&
    Number.isFinite(p.limite) &&
    p.limite >= 0
  )
    q.limite = Math.trunc(p.limite);
  if (
    typeof p.offset === 'number' &&
    Number.isFinite(p.offset) &&
    p.offset >= 0
  )
    q.offset = Math.trunc(p.offset);

  return q;
};

export interface GetTenantsResponse {
  items: ITenantDataFactory[];
  totalCount: number;
}

const getTenants = async (
  params?: GetTenantsParams,
): Promise<{ data: GetTenantsResponse } | ApiException> => {
  try {
    const { data } = await Api.get<GetTenantsResponse>('/DataFactory/Tenants', {
      params: buildQueryParams(params),
      headers: {
        idTenant: 'gvdasa-cloud',
      },
    });
    return { data };
  } catch (error: any) {
    console.error('Erro ao buscar tenants:', error);
    const message =
      error?.response?.data?.erro || error?.message || 'Erro desconhecido';
    return new ApiException(message);
  }
};

const getAtivos = async (): Promise<{ data: string[] } | ApiException> => {
  try {
    const { data } = await Api.get<string[]>('/DataFactory/Tenants/ativos', {
      headers: {
        idTenant: 'gvdasa-cloud',
        // Authorization is expected to be handled by the Api axios instance (interceptor) in production
      },
    });
    return { data };
  } catch (error: any) {
    console.error('Erro ao buscar tenants ativos:', error);
    const message =
      error?.response?.data?.errors?.default ||
      error?.response?.data?.erro ||
      error?.message ||
      'Erro desconhecido';
    return new ApiException(message);
  }
};

interface IMonitoraramentoResponse {
  valor: string;
}
interface IUsuarioBancoResponse {
  valor: string;
}

const monitoraramento = async (
  idTenant: string,
): Promise<{ data: IMonitoraramentoResponse } | ApiException> => {
  try {
    if (typeof idTenant !== 'string' || idTenant.trim() === '') {
      return new ApiException('Parâmetro idTenant é obrigatório.');
    }
    const { data } = await Api.post<IMonitoraramentoResponse>(
      '/DataFactory/Tenants/monitora',
      { idTenant },
      {
        headers: {
          idTenant: 'gvdasa-cloud',
        },
      },
    );
    return { data };
  } catch (error: any) {
    console.error('Erro ao monitorar tenant:', error.data.data);
    const message = error.data.data.erro || 'Erro desconhecido';
    return new ApiException(message);
  }
};

const getById = async (
  idTenant: string,
): Promise<{ data: ITenantDataFactory } | ApiException> => {
  try {
    if (typeof idTenant !== 'string' || idTenant.trim() === '') {
      return new ApiException('Parâmetro idTenant é obrigatório.');
    }
    const { data } = await Api.get<ITenantDataFactory>(
      `/DataFactory/Tenants/${encodeURIComponent(idTenant.trim())}`,
      {
        headers: {
          idTenant: 'gvdasa-cloud',

          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      },
    );
    return { data };
  } catch (error: any) {
    console.error('Erro ao obter tenant por id:', error);
    const message =
      error?.response?.data?.errors?.default ||
      error?.response?.data?.erro ||
      error?.message ||
      'Erro desconhecido';
    return new ApiException(message);
  }
};

const aplicaMigration = async (
  idTenant: string,
): Promise<{ data: null } | ApiException> => {
  try {
    if (typeof idTenant !== 'string' || idTenant.trim() === '') {
      return new ApiException('Parâmetro idTenant é obrigatório.');
    }

    await Api.post(
      `/DataFactory/Tenants/aplicar-migrations/${encodeURIComponent(idTenant.trim())}`,
      {},
      {
        headers: {
          idTenant: 'gvdasa-cloud',
        },
      },
    );

    return { data: null };
  } catch (error: any) {
    console.error('Erro ao aplicar migrations:', error);
    const message =
      error?.response?.data?.errors?.default ||
      error?.response?.data?.erro ||
      error?.message ||
      'Erro desconhecido';
    return new ApiException(message);
  }
};

const criaUsuario = async (
  idTenant: string,
): Promise<{ data: IUsuarioBancoResponse } | ApiException> => {
  try {
    if (typeof idTenant !== 'string' || idTenant.trim() === '') {
      return new ApiException('Parâmetro idTenant é obrigatório.');
    }
    const { data } = await Api.post<IUsuarioBancoResponse>(
      `/DataFactory/UsuariosBanco/${encodeURIComponent(idTenant.trim())}`,
      {},
      {
        headers: {
          idTenant: 'gvdasa-cloud',
        },
      },
    );
    return { data };
  } catch (error: any) {
    console.error('Erro ao criar usuário no banco:', error);
    const message =
      error?.response?.data?.errors?.default ||
      error?.response?.data?.erro ||
      error?.message ||
      'Erro desconhecido';
    return new ApiException(message);
  }
};

const atualizaUsuario = async (
  idTenant: string,
): Promise<{ data: IUsuarioBancoResponse } | ApiException> => {
  try {
    if (typeof idTenant !== 'string' || idTenant.trim() === '') {
      return new ApiException('Parâmetro idTenant é obrigatório.');
    }
    const { data } = await Api.put<IUsuarioBancoResponse>(
      `/DataFactory/UsuariosBanco/${encodeURIComponent(idTenant.trim())}`,
      {},
      {
        headers: {
          idTenant: 'gvdasa-cloud',
        },
      },
    );
    return { data };
  } catch (error: any) {
    console.error('Erro ao atualizar usuário no banco:', error);
    const message =
      error?.response?.data?.errors?.default ||
      error?.response?.data?.erro ||
      error?.message ||
      'Erro desconhecido';
    return new ApiException(message);
  }
};

const deleteMonitoramento = async (
  idTenant: string,
): Promise<{ data: null } | ApiException> => {
  try {
    if (typeof idTenant !== 'string' || idTenant.trim() === '') {
      return new ApiException('Parâmetro idTenant é obrigatório.');
    }

    await Api.delete(
      `/DataFactory/Tenants/${encodeURIComponent(idTenant.trim())}`,
      {
        headers: {
          idTenant: 'gvdasa-cloud',
        },
      },
    );

    return { data: null };
  } catch (error: any) {
    console.error('Erro ao desativar monitoramento do tenant:', error);
    const message =
      error?.response?.data?.errors?.default ||
      error?.response?.data?.erro ||
      error?.message ||
      'Erro desconhecido';
    return new ApiException(message);
  }
};

export const DataFactoryService = {
  getTenants,
  getAtivos,
  monitoraramento,
  getById,
  aplicaMigration,
  criaUsuario,
  atualizaUsuario,
  deleteMonitoramento,
};
